import React, { useEffect, useState } from 'react';
import {
  Table,
  UxPerformanceIndicator,
  UxPagination,
} from "botbit-ui-components";
import { getLocalSEOTableData } from '../../../api/dashboardV2Api';
import './localSEOTable.css';
import {
    GoogleMapsIcon, 
    NavigationIcon,
    CallIcon,
    VisitsIcon,
    WebsiteClicksIcon, 
    ChartArrowIcon, 
    ReconnectGBPIcon
} from './localSEOTableIcons';
import translate from "../../../config/translations/translate";
import { LocalSeoStoreChartModal } from '../Modals/localSeoStoreChartModal/LocalSeoStoreChartModal';
import DashboardPeriodSelector from '../dashboardPeriodSelector/DashboardPeriodSelector';
import withEventTracking, { trackers } from '../../../config/withEventTracking';

const ORDER_TYPE_MAP = {
    'ranking': {
      asc: 'AVERAGE_RANKING_ASC',
      desc: 'AVERAGE_RANKING_DESC'
    },
    'gbpAuditScore': {
      asc: 'GBP_AUDIT_ASC',
      desc: 'GBP_AUDIT_DESC'
    },
    'navigation': {
      asc: 'NAVIGATION_ASC',
      desc: 'NAVIGATION_DESC'
    },
    'calls': {
      asc: 'CALLS_ASC',
      desc: 'CALLS_DESC'
    },
    'visits': {
      asc: 'VISITS_ASC',
      desc: 'VISITS_DESC'
    },
    'websiteClicks': {
      asc: 'WEBSITE_CLICKS_ASC',
      desc: 'WEBSITE_CLICKS_DESC'
    },
    'orders': {
      asc: 'ORDERS_ASC',
      desc: 'ORDERS_DESC'
    },
    'bookings': {
      asc: 'BOOKINGS_ASC',
      desc: 'BOOKINGS_DESC'
    },
    'menuClicks': {
      asc: 'MENU_CLICKS_ASC',
      desc: 'MENU_CLICKS_DESC'
    },
    'engagementRate': {
      asc: 'ENGAGEMENT_RATE_ASC',
      desc: 'ENGAGEMENT_RATE_DESC'
    }
  };
  

export const formatStoreMetrics = (storeData) => ({
    visits: storeData.visits || { value: 0, variationPercentage: 0 },
    walkIns: storeData.navigation || { value: 0, variationPercentage: 0 },
    websiteClicks: storeData.websiteClicks || { value: 0, variationPercentage: 0 },
    calls: storeData.calls || { value: 0, variationPercentage: 0 },
    orders: storeData.orders || { value: 0, variationPercentage: 0 },
    bookings: storeData.bookings || { value: 0, variationPercentage: 0 },
    menuClicks: storeData.menuClicks || { value: 0, variationPercentage: 0 },
    engagementRate: storeData.engagementRate.value && storeData.engagementRate.value > 0 ? storeData.engagementRate.value : 0
});

const localSEOTable = ({companyId, period, t, timePeriodOptions, selectedPeriod, handlePeriodChange, windowWidth, trackEvent, setSelectedPeriod}) => {

    const [totalPages, setTotalPages] = useState(0);
    const [totalLocations, setTotalLocations] = useState(0);
    const [selectedPage, setSelectedPage] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedStore, setSelectedStore] = useState(null);
    const [currentOrder, setCurrentOrder] = useState({ name: 'ranking', direction: 'desc' });

    const isSmallScreen = windowWidth <= 1240;

    const filterValues = { timePeriod: period, page: selectedPage, pageSize: 10 }

    useEffect(() => {
        setSelectedPage(0);
        filterValues.page = 0;
        filterValues.timePeriod = period && period.value ? period.value : period;
    }, [period, currentOrder])

    const getTableData = async (page, order = currentOrder, filters) => {
        const orderChanged = currentOrder.name !== order.name || currentOrder.direction !== order.direction;
        if(orderChanged) setCurrentOrder(order);
        const mappedOrderType = ORDER_TYPE_MAP[order.name][order.direction];

        try {
            const response = await getLocalSEOTableData(
                companyId,
                orderChanged ? 0 : filters.page,
                filters.timePeriod,            
                filters.pageSize,
                mappedOrderType
            );
            setTotalPages(response.data.data.totalPages);
            setTotalLocations(response.data.data.totalLocations);

            return response;
        } catch (error) {
            console.error('Error en getTableData:', error);
            throw error;
        }
    }

    const handlePageChange = (newPage) => {
        setSelectedPage(newPage - 1);
    };

    // función que define el contenido para filas sobrescritas
    const rowOverrideContent = (element) => {
        return (
            <div className="custom-row">
                <span className="local-seo-table-various-spans-number-bold"> {element.name} </span>
                <div className="custom-row-message-container">
                    <ReconnectGBPIcon/>
                    <span className="custom-row-message-span"> 
                        { element.lastSyncAtIsOlderThanAWeek ? t('columns.reconnectGBPMessage') : t('columns.disabledStoreMessage') } 
                    </span>
                    <a href=
                            {element.lastSyncAtIsOlderThanAWeek ? `https://admin.rayapp.io/socialaccounts` : `https://help.rayapp.io/es/articles/11326252-como-verificar-tu-perfil-de-negocio-en-google-business-profile`} 
                        target="_blank" rel="noopener noreferrer" className="custom-row-message-span-link"> {element.lastSyncAtIsOlderThanAWeek ? t('columns.reconnectGBP') : t('columns.verifyGBP')} </a>
                </div>
            </div>
        );
    };


    const toggleModal = () => setIsModalOpen(!isModalOpen);

    const handleCreateStoreObject = (row) => {
        const store = {
            id: row.storeId,
            name: row.name,
            companyId: companyId,
            metrics: formatStoreMetrics(row)
        };
        setSelectedStore(store);
      };
    const styleNumberWithPerformanceIndicator = ({display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', maxWidth: '100px'});
    const styleDivNoDataComparison = ({ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '30px'});
    const columns = [
        {
            dataField: 'storeName',
            title: 
                <div className="local-seo-table-title-container">
                    <span className="local-seo-table-title-span-store-name">
                        { t('columns.stores') + ` (${totalLocations})`}
                    </span>
                        
                     
                </div>,
            size: 22,
            formatter: (cellValue, row) => {
                const storeName = row.name;
                return (
                    <span className="local-seo-table-various-spans-number-bold"> {storeName} </span>
                )
            },
            
        },
        {
           
            dataField: 'ranking',
            title: 
                <div className="local-seo-table-title-container">
                    <span className="local-seo-table-title-span"> {t('columns.ranking')} </span> 
                </div>,
            sortable: true,
            size: isSmallScreen ? 11 : 9,
            formatter: (cellValue, row) => (
                <span className="local-seo-table-various-spans-number">
                    {Number(row.ranking).toFixed(1)}
                </span>
            ),
            
        },
        {
            dataField: 'gbpAuditScore',
            title: 
                <div className="local-seo-table-title-container">
                    <span className="local-seo-table-title-span">
                        {t('columns.gbpAuditScore')}
                    </span>
                </div>,
            sortable: true,
            size: isSmallScreen ? 14 : 10,
            formatter: (cellValue, row) => <span className="local-seo-table-various-spans-number"><b>{row.gbpAuditScore ? row.gbpAuditScore : 0}</b>/100</span>,
            
        },
        {
            dataField: 'navigation',
            title: 
                <div className="local-seo-table-title-container">
                    
                    <span className="local-seo-table-title-span">
                        <NavigationIcon/>
                        {t('columns.navigation')}
                    </span>

                </div>,
            sortable: true,
            size: isSmallScreen ? 17 : 14,
            formatter: (cellValue, row) => (
                <div style={styleNumberWithPerformanceIndicator}>
                    <span className="local-seo-table-various-spans-number">{row.navigation ? row.navigation.value : 0}</span>
                    {(row.navigation && row.navigation.variationPercentage !== 0) ? (
                        <div 
                            title={t('columns.comparisonTooltip')}
                            style={{ cursor: 'pointer' }}
                        >
                            <UxPerformanceIndicator value={row.navigation.variationPercentage ? row.navigation.variationPercentage : 0} />
                        </div>
                    ) : (
                        
                        <div 
                            title={t('columns.noDataComparisonTooltip')}
                            style={styleDivNoDataComparison}
                        >
                            <span className="local-seo-table-various-spans-number">-</span>
                        </div>
                    )}
                </div>
            )
        },
        {
            dataField: 'calls',
            title: 
                <div className="local-seo-table-title-container">
                    <span className="local-seo-table-title-span">
                        <CallIcon/>
                        {t('columns.calls')}
                    </span>
                </div>,
            sortable: true,
            size: isSmallScreen ? 17 : 14,
            formatter: (cellValue, row) => (
                <div style={styleNumberWithPerformanceIndicator}>
                    <span className="local-seo-table-various-spans-number">{row.calls ? row.calls.value : 0}</span>
                    {(row.calls && row.calls.variationPercentage !== 0) ? (
                        <div 
                            title={t('columns.comparisonTooltip')}
                            style={{ cursor: 'pointer' }}
                        >
                            <UxPerformanceIndicator value={row.calls.variationPercentage ? row.calls.variationPercentage : 0} />
                        </div>
                    ) : (
                        
                        <div 
                            title={t('columns.noDataComparisonTooltip')}
                            style={styleDivNoDataComparison}
                        >
                            <span className="local-seo-table-various-spans-number">-</span>
                        </div>
                    )}
                </div>
            )
            
        },
        {
            dataField: 'visits',
            title: 
                <div className="local-seo-table-title-container">
                    <span className="local-seo-table-title-span">
                        <VisitsIcon/>
                        {t('columns.visits')}
                    </span>
                </div>,
            sortable: true,
            size: isSmallScreen ? 17 : 17,
            formatter: (cellValue, row) => (
                <div style={{...styleNumberWithPerformanceIndicator, maxWidth: '115px'}}>
                    <span className="local-seo-table-various-spans-number">{row.visits ? row.visits.value : 0}</span>
                    {(row.visits && row.visits.variationPercentage !== 0) ? (
                        <div 
                            title={t('columns.comparisonTooltip')}
                            style={{ cursor: 'pointer' }}
                        >
                            <UxPerformanceIndicator value={row.visits.variationPercentage ? row.visits.variationPercentage : 0} />
                        </div>
                    ) : (
                        
                        <div 
                            title={t('columns.noDataComparisonTooltip')}
                            style={styleDivNoDataComparison}
                        >
                            <span className="local-seo-table-various-spans-number">-</span>
                        </div>
                    )}
                </div>
            )
            
            
        },
        ...(isSmallScreen ? [] : [{
            dataField: 'engagementRate',
            title: 
                <div className="local-seo-table-title-container">
                    <span className="local-seo-table-title-span">
                        <WebsiteClicksIcon/>
                        <span className="navigation-text">
                            {t('columns.engagementRate')}
                        </span>  
                    </span>
                </div>,
            size: 11,
            sortable: true,
            formatter: (cellValue, row) => (
                <div style={styleNumberWithPerformanceIndicator}>
                    <span className="local-seo-table-various-spans-number">{row.engagementRate && row.engagementRate.value ? row.engagementRate.value.toFixed(1) + "%" : '-'}</span>
                </div>
            )
        }]),
        {
            dataField: 'details',
            title: '',
            size: 3,    
            formatter: (cellValue, row) => {
                const handleRedirect = () => {
                    handleCreateStoreObject(row);
                    setIsModalOpen(true);
                    trackEvent('Dashboard_locaoseo_ver_detalle_clicked', {
                        storeId: row.storeId,
                        companyId: companyId,
                        period: selectedPeriod
                    }, false, [trackers.MIXPANEL]);
                };

                return (
                    <button type="button" className="local-seo-table-details-button" onClick={handleRedirect}>
                        <ChartArrowIcon />
                    </button>
                )
            }
            
            
        },
      

    ]

    return (
        <>
            <div className='local-seo-table-container'>
                <div className='local-seo-table-header-container'>
                    
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px', width: 227, height:57}}>
                        <GoogleMapsIcon />
                        <h2 className="local-seo-table-title">{t('title')}</h2>
                        
                    </div>
                    <DashboardPeriodSelector 
                        timePeriodOptions={timePeriodOptions}
                        t={t}
                        selectedPeriod={selectedPeriod}
                        handlePeriodChange={handlePeriodChange}
                        setSelectedPeriod={setSelectedPeriod}
                    />
                    
                </div>
                <div className="local-seo-table-body-and-pagination-container">
                    <Table
                        pagination={false}
                        getData={getTableData}
                        filterValues={filterValues}
                        columns={columns}
                        showMobileColumnTitles={true}
                        dataPath={"data.data.locationRankingAndMetrics"}
                        elementKey={"storeId"}                        
                        rowOverrideCondition={(element) => element.lastSyncAtIsOlderThanAWeek || element.locationEnabled === false}
                        rowOverrideContent={rowOverrideContent}

                    />
                    <div className="pagination-container">
                        {totalPages > 1 && (                
                            <UxPagination
                                totalPages={totalPages}
                                // las paginas de la api son 0-based, por eso le sumo 1. (el paginador es 1-based)
                                currentPage={filterValues.page + 1}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </div>       
                </div>
                <div className="local-seo-table-modal-container">
                    <LocalSeoStoreChartModal
                        store={selectedStore}
                        isOpen={isModalOpen}
                        toggle={toggleModal}
                        selectedPeriod={period}
                        t={t}
                    />
                </div>
            </div>
        </>
    )
}

localSEOTable.translations = {
    title: {
        en: "Google Maps Ranking",
        es: "Ranking Google Maps"
    },
    timePeriod: {
        label: {
          es: "Período de tiempo",
          en: "Time period"
        },
        options: {
            TODAY: {
                es: "Hoy",
                en: "Today"
            },
            YESTERDAY: {
                es: "Ayer",
                en: "Yesterday"
            },
            LAST_24_HOURS: {
                es: "Últimas 24 horas",
                en: "Last 24 hours"
            },
            LAST_7_DAYS: {
                es: "Últimos 7 días",
                en: "Last 7 days"
            },
            LAST_30_DAYS: {
                es: "Últimos 30 días",
                en: "Last 30 days" 
            },
            PAST_MONTH: {
                es: "Mes pasado",
                en: "Past Month"
            },
            THIS_MONTH: {
                es: "Este mes",
                en: "This month"
            },
            LAST_3_MONTHS: {
                es: "Últimos 3 meses",
                en: "Last 3 months"
            },
            LAST_6_MONTHS: {
                es: "Últimos 6 meses",
                en: "Last 6 months"
            },
            LAST_12_MONTHS: {
                es: "Últimos 12 meses", 
                en: "Last 12 months"
            },
            THIS_YEAR: {
                es: "Este año",
                en: "This year"
            },
            CUSTOM: {
                es: "Personalizado",
                en: "Custom"
            }
        }
    },
    columns: {
        stores: {
          es: "Locales",
          en: "Stores"
        },
        ranking: {
          es: "Ranking promedio",
          en: "Average Ranking",
        },
        gbpAuditScore: {
          es: "GBP Audit",
          en: "GBP Audit",
        },
        navigation: {
            es: "Walk-Ins",
            en: "Walk-Ins",
        },
        navigation12Months: {
          es: "Walk-Ins (12 meses atrás)",
          en: "Walk-Ins (12 months ago)",
        }, 
        calls: {
            es: "Llamadas",
            en: "Calls",
        },
        visits: {
            es: "Visitas",
            en: "Visits",
        },
        websiteClicks: {
            es: "Website Clicks",
            en: "Website Clicks"
        },
        engagementRate: {
            es: "Eng. Rate",
            en: "Eng. Rate"
        },
        comparisonTooltip: {
            es: "Esta comparación muestra el porcentaje de cambio entre el período y el mismo período del año anterior. Ejemplo: El mes pasado compara abril 2025 con abril 2024. Si no hay suficientes datos para el período anterior, esta comparación se ocultará.",
            en: "This comparison shows the percent of change between the period and the same period the year before. Example: Last month compares April 2025 with April 2024. If there is not enough data for the previous period, this comparison will be hidden."
        },
        noDataComparisonTooltip: {
            es: "Esta comparación no aplica, ya sea porque no hay datos suficientes o porque el periodo a comparar en esta métrica suma 0.",
            en: "This comparison does not apply, either because there is not enough data or because the previous period sum of values equals 0."
        },
        reconnectGBP: {
            es: " Reconectar GBP",
            en: " Reconnect GBP"
        },
        reconnectGBPMessage: {
            es: "El opt-in con Google para este local está vencido. ",
            en: "The opt-in with Google for this location is expired. "
        },
        verifyGBP: {
            es: "Más información",
            en: "More information"
        },
        disabledStoreMessage: {
            es: "Este local no está verificado en Google.",
            en: "This location is not verified in Google."
        }
    }
}

export default withEventTracking(translate(localSEOTable));

